// Article bodies are untrusted publisher HTML. DOMPurify keeps prose, media and
// links, drops scripts, styles and event handlers, and only allows video embeds
// from known players.
import DOMPurify from "dompurify";
import { EMBED_PLAYERS, embedLinks, imageKey, photoSizes, PHOTO_SIZES } from "@vladrss/core";

// the players the core allows (figures.ts EMBED_PLAYERS), from https only
const EMBED = new RegExp(`^https:${EMBED_PLAYERS.source}`, "i");
const CHART = /^https:\/\/(?:flo\.uri\.sh|datawrapper\.dwcdn\.net)\//;

// a page may carry one player twice, for small and large screens, showing one at a time
let players = new Set<string>();
DOMPurify.addHook("uponSanitizeElement", (node, data) => {
  if (data.tagName === "iframe") {
    const src = (node as Element).getAttribute("src") ?? "";
    const player = src.split("?")[0]!;
    if (!EMBED.test(src) || players.has(player)) node.parentNode?.removeChild(node);
    else players.add(player);
  }
});
/** A srcset with a relative address would resolve against this app, not the publisher; the absolute src is enough. */
const relativeSrcset = (set: string | null) => !!set && set.split(",").some((p) => !/^https?:\/\//i.test(p.trim()));

DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  // a player may run and go full screen, never take over the app
  if (node.tagName === "IFRAME") { node.setAttribute("loading", "lazy"); node.setAttribute("sandbox", "allow-scripts allow-same-origin allow-presentation allow-popups allow-popups-to-escape-sandbox"); if (node.getAttribute("src")?.includes("instagram.com")) node.classList.add("ig"); if (CHART.test(node.getAttribute("src") ?? "")) node.classList.add("chart"); }
  if (node.tagName === "A") { node.setAttribute("target", "_blank"); node.setAttribute("rel", "noopener noreferrer"); }
  if ((node.tagName === "IMG" || node.tagName === "SOURCE") && relativeSrcset(node.getAttribute("srcset"))) node.removeAttribute("srcset");
  if (node.tagName === "IMG") {
    const lazy = node.getAttribute("data-src") ?? node.getAttribute("data-lazy-src");
    if (lazy && (!node.getAttribute("src") || node.getAttribute("src")!.startsWith("data:"))) node.setAttribute("src", lazy);
    // a photo served at one large size from a server that makes others: the screen picks its size
    const sized = !node.getAttribute("srcset") && photoSizes(node.getAttribute("src") ?? "");
    if (sized) { node.setAttribute("src", sized.src); node.setAttribute("srcset", sized.srcset); }
    // sizes for the reader's column, not the publisher's layout
    if (node.getAttribute("srcset")) node.setAttribute("sizes", PHOTO_SIZES);
    node.setAttribute("loading", "lazy");
    node.setAttribute("decoding", "async");
    node.setAttribute("referrerpolicy", "no-referrer");
    const w = Number(node.getAttribute("width")), h = Number(node.getAttribute("height"));
    if (w && w <= 120 && (!h || h <= 120)) node.classList.add("tiny");
    else if (w && h) (node as HTMLElement).style.aspectRatio = `${w} / ${h}`;
  }
});

export function sanitize(html: string, author?: string): string {
  // a paragraph that is only a YouTube or Instagram link, and Instagram's embed block, become players
  players = new Set();
  const clean = DOMPurify.sanitize(embedLinks(html), {
    ADD_TAGS: ["iframe"],
    ADD_ATTR: ["allowfullscreen", "title", "frameborder", "data-src", "data-lazy-src", "loading", "decoding", "referrerpolicy", "target"],
    FORBID_TAGS: ["style", "form", "input", "button", "select", "textarea"],
    FORBID_ATTR: ["style", "class", "id"],
  });
  return oncePerPhoto(clean, author);
}

/**
 * Each photo once: pages often carry the lead photo again in the body, or one photo at two
 * sizes for small and large screens. The first stays; a repeat goes, with its figure when
 * that held nothing else. Tiny images (icons, quote marks) may repeat. The writer's own
 * photo (an image captioned with the author's name) goes too.
 */
function oncePerPhoto(html: string, author?: string): string {
  const name = (s: string | null | undefined) => (s ?? "").trim().toLowerCase().replace(/[‘’`´]/g, "'");
  const writer = name(author);
  if (!/<img\b/i.test(html)) return html;
  const t = document.createElement("template");
  t.innerHTML = html;
  const seen = new Set<string>();
  for (const img of t.content.querySelectorAll("img")) {
    // the writer's photo from the byline (The Verge): the reader names the author already
    if (writer && name(img.getAttribute("alt")) === writer) { (img.closest("figure") ?? img).remove(); continue; }
    if (img.classList.contains("tiny")) continue;
    const key = imageKey(img.getAttribute("src") ?? "");
    if (!key) continue;
    if (!seen.has(key)) { seen.add(key); continue; }
    const fig = img.closest("figure");
    if (fig && fig.querySelectorAll("img, video, iframe").length === 1) fig.remove();
    else (img.closest("picture") ?? img).remove();
  }
  // photos the publisher set side by side (a block of nothing but two or three figures)
  for (const row of t.content.querySelectorAll("div, p")) {
    const kids = [...row.children];
    if (kids.length >= 2 && kids.length <= 3 && kids.every((k) => k.tagName === "FIGURE") && ![...row.childNodes].some((n) => n.nodeType === 3 && n.textContent?.trim()))
      for (const k of kids) k.setAttribute("data-row", String(kids.length));
  }
  return t.innerHTML;
}
